import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import ProgressBar from '../../common/ProgressBar';
import './styles.scss';
import {
    setSelectedChallenge,
    setSelectedChallengeId,
    challengeRequested,
    setParentChallenge,
} from './actions';
import LoadingComponent from '../../common/LoadingComponent';
import usePrevious from '../../common/hooks/usePrevious';
import { noop } from '../../common/propTypes';
import ErrorBoundary from '../../common/ErrorBoundary';
import ChallengeBanner from '../../common/ChallengeBanner';
import InfoCard from '../../common/InfoCard';
import ConnectedTopTags from './ConnectedTopTags';
import ConnectedMap from './ConnectedMap';
import ConnectedImpactCard from './ConnectedImpactCard';
import ConnectedTopContributors from './ConnectedTopContributors';
import ConnectedChallenges from './ConnectedChallenges';
import {
    getChallenge,
    getChallengeLoading,
    getParentChallenge,
} from './selectors';

function Campaign({
    setChallenge,
    fetchChallenge,
    setChallengeId,
    selectedChallenge,
    challengeLoading,
    setParentChallenge,
    parentChallenge,
}) {
    // scroll top top
    useEffect(() => {
        window.scrollTo(0, 0);
        setParentChallenge();
        return () => {
            setParentChallenge();
        };
    }, []);

    const location = useLocation();
    const locationId = location.pathname.split('/')[2];
    const prevLocation = usePrevious(locationId);

    // run before the component mounts
    // the challengeId in state must be reset
    setChallengeId(locationId);

    useEffect(() => {
        setChallenge({});
        if (location.state) {
            fetchChallenge(location.state.challenge.id);
        } else {
            fetchChallenge(locationId);
        }
    }, []);

    useEffect(() => {
        if (prevLocation && prevLocation !== locationId) {
            setChallenge({});
            fetchChallenge(locationId);
        }
    }, [location]);

    const navigation = [];
    navigation.push({ title: 'Campaigns', path: '/campaigns' });
    if (parentChallenge) {
        navigation.push({
            title: parentChallenge.title,
            path: `/challenges/${parentChallenge.id}`,
        });
    }
    navigation.push({ title: selectedChallenge.title, path: '#' });

    const mainContent = selectedChallenge ? (
        <div>
            <div className="row">
                <div className="col px-0">
                    <ChallengeBanner
                        title={selectedChallenge.title}
                        image={selectedChallenge.banner}
                        iconClass="icon-flag"
                        badge={'Campaign'}
                        navigation={navigation}
                    />
                </div>
            </div>
            <div className="spacer"></div>
            <div className="row">
                <div className="col">
                    <div className="content-box card">
                        <div className="row">
                            <div className="col-md-7 col-sm-12">
                                <div className="row h-100">
                                    <div className="col-12 align-self-start">
                                        <div className="challengesWrapper pr-0 pr-md-5">
                                            <h3 className="titleStyle2">
                                                Description
                                            </h3>
                                            <p className="paraStyleTwo">
                                                {selectedChallenge.description}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 align-self-end">
                                        <h3 className="titleStyle2">
                                            Pickup Quantity
                                        </h3>
                                        <h3 className="title-secondary progress-font">
                                            {selectedChallenge.pickups}
                                            <span className="progressData">
                                                /{selectedChallenge.target}
                                            </span>
                                        </h3>
                                        <ProgressBar
                                            progress={
                                                selectedChallenge.progress
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <InfoCard
                                createDate={selectedChallenge.createDate}
                                startDate={selectedChallenge.startDate}
                                endDate={selectedChallenge.endDate}
                                userName={selectedChallenge.createdBy}
                                userImage={selectedChallenge.creatorThumbnail}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <LoadingComponent isLoading={challengeLoading} />
    );

    return (
        <div>
            {mainContent}
            <div className="spacer" />
            <div className="challengesWrapper">
                <div className="row">
                    <div className="col-5">
                        <ErrorBoundary>
                            <ConnectedTopContributors />
                        </ErrorBoundary>
                    </div>
                    <div className="col-7 top-tags">
                        <ErrorBoundary>
                            <ConnectedTopTags />
                        </ErrorBoundary>
                    </div>
                </div>
            </div>
            <div className="spacer" />
            <ConnectedChallenges challenge={selectedChallenge} />
            <div className="spacer" />
            <div className="row">
                <div className="col-md-12 mapWrapper impactMap">
                    <ErrorBoundary>
                        <ConnectedMap />
                    </ErrorBoundary>
                    <div className="impact-container">
                        <ErrorBoundary>
                            <ConnectedImpactCard />
                        </ErrorBoundary>
                    </div>
                </div>
            </div>
        </div>
    );
}

Campaign.propTypes = {
    // function to call before the component mounts
    // this sets the specific challenge id in state
    setChallengeId: PropTypes.func,
    setChallenge: PropTypes.func,
    // function to fetch the challenge if the user
    // did not navigate with challenge in state and
    // the challenge has to be fetched via API call
    fetchChallenge: PropTypes.func,
    setParentChallenge: PropTypes.func,
    selectedChallenge: PropTypes.object,
    parentChallenge: PropTypes.object,
    challengeLoading: PropTypes.bool,
};

Campaign.defaultProps = {
    onLoad: noop,
    fetchChallenge: noop,
    selectedChallenge: null,
    challengeLoading: false,
};

const mapStateToProps = (state) => {
    return {
        selectedChallenge: getChallenge(state),
        challengeLoading: getChallengeLoading(state),
        parentChallenge: getParentChallenge(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setChallengeId: (challengeId) =>
            dispatch(setSelectedChallengeId(challengeId)),
        setChallenge: (challenge) => dispatch(setSelectedChallenge(challenge)),
        fetchChallenge: (challengeId) =>
            dispatch(challengeRequested(challengeId)),
        setParentChallenge: () => dispatch(setParentChallenge(null)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Campaign);
